<template>
  <overall-settings v-bind="$props">
    <section v-if="fields" class="c-mini-banner" :style="rootStyle">
      <ContentGrid :desktop-default-grid="[1, 22]" :mobile-default-grid="[1, 14]">
        <div class="c-mini-banner__inner">
          <div class="c-mini-banner__content">
            <h2 v-if="title" v-html="title" />
            <p v-if="description" class="c-mini-banner__description">{{ description }}</p>
          </div>

          <div v-if="buttonFields" class="c-mini-banner__action">
            <SiteButton :fields="buttonFields" />
          </div>
        </div>
      </ContentGrid>
    </section>
  </overall-settings>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { MiniBannerProps } from '@/types/components/MiniBanner';
import type { SiteButtonFields } from '@/types/elements/site-button';
import OverallSettings from '@/sections/overall-settings.vue';
import utils from '@/utils';

const props = defineProps<MiniBannerProps>();
const fields = computed(() => props.fields);

const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const title = computed(() => fields.value?.title?.value || '');
const description = computed(() => fields.value?.description?.value || '');

const buttonFields = computed<SiteButtonFields | null>(() => {
  const link = fields.value?.buttonLink?.value;
  const text = fields.value?.buttonText?.value || link?.text || '';
  if (!text && !link?.href) return null;

  return {
    text: { value: text },
    link: {
      value: {
        href: link?.href || '#',
        text,
        target: link?.target || '_self',
      },
    },
    type: {
      id: 'primary',
      name: 'primary',
      fields: {
        phrase: {
          value: 'primary',
        },
      },
    },
    theme: {
      id: 'green',
      name: 'green',
      fields: {
        phrase: {
          value: 'green',
        },
      },
    },
  };
});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-mini-banner {
  padding: 16px 0;
  background: transparent;

  &__inner {
    display: grid;
    gap: 20px;
    padding: 22px 20px;
    background: rgba(241, 244, 241, 1);
    border-radius: 28px;
  }

  &__content {
    min-width: 0;
  }

  &__title {
    margin: 0;
    color: #090f1d;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 600;
  }

  &__description {
    margin: 14px 0 0;
    color: #4b5563;
    font-size: 16px;
    line-height: 1.45;
    font-weight: 400;
  }

  &__action {
    display: flex;
    align-items: center;
  }

  .e-site-button {
    min-width: 100%;
    height: 48px;
    padding: 0 26px;
    border-radius: 999px;
    font-size: 16px;
    line-height: 1;
  }

  .e-site-button.primary.theme-green {
    background: #739b78;
    color: $white;

    &:hover,
    &:focus-visible {
      background: #5f8664;
      color: $white;
    }
  }

  @include tablet {
    &__inner {
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 28px;
      padding: 26px 34px;
    }

    &__title {
      font-size: 18px;
    }

    &__description {
      font-size: 16px;
    }

    .e-site-button {
      min-width: 286px;
      height: 48px;
      padding: 0 34px;
      font-size: 24px;
    }
  }
}
</style>
